import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Store,
  Settings,
  Eye,
  Edit,
  ExternalLink,
  Globe,
  ShoppingBag,
  Package,
  Plus,
  MoreVertical,
  Palette,
  CheckCircle,
  Clock,
  AlertCircle } from
'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from
"@/shared/components/ui/dropdown-menu";
import { Link } from 'react-router-dom';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const STATUS_CONFIG = {
  draft: { icon: Clock, color: 'bg-gray-100 text-gray-700', label: 'Draft' },
  testing: { icon: AlertCircle, color: 'bg-amber-100 text-amber-700', label: 'Testing' },
  published: { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700', label: 'Published' },
  suspended: { icon: AlertCircle, color: 'bg-red-100 text-red-700', label: 'Suspended' }
};

export const EcommerceStores = () => {const { t } = useTranslation("ecommerce");
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ecommerce/stores`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setStores(data.stores || []);
      }
    } catch (err) {
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (storeId) => {
    try {
      const res = await fetch(`${API_URL}/api/ecommerce/stores/${storeId}/publish`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Store published successfully!');
        fetchStores();
      }
    } catch (err) {
      toast.error('Failed to publish store');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="ecommerce-stores">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
              <Store className="h-7 w-7 text-white" />
            </div>{t("myStores", "My Stores")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageYourOnline", "Manage your online storefronts and configurations")}

          </p>
        </div>
        <Link to="/ecommerce/builder">
          <Button className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
            <Plus className="h-4 w-4" />{t("createStore", "Create Store")}

          </Button>
        </Link>
      </div>

      {/* Stores Grid */}
      {stores.length === 0 ?
      <Card className="card-elevated">
          <CardContent className="p-12 text-center">
            <Store className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-xl font-semibold mb-2">{t("noStoresYet", "No stores yet")}</h3>
            <p className="text-muted-foreground mb-6">{t("createYourFirst", "Create your first online store to start selling")}

          </p>
            <Link to="/ecommerce/builder">
              <Button className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-600">
                <Plus className="h-4 w-4" />{t("createYourFirst", "Create Your First Store")}

            </Button>
            </Link>
          </CardContent>
        </Card> :

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {stores.map((store) => {
          const statusConfig = STATUS_CONFIG[store.status] || STATUS_CONFIG.draft;
          const StatusIcon = statusConfig.icon;

          return (
            <Card key={store.id} className="group overflow-hidden card-elevated hover:shadow-xl transition-all duration-300">
                {/* Store Header with Theme Colors */}
                <div
                className="h-24 relative overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${store.theme_config?.primary_color || '#10b981'} 0%, ${store.theme_config?.secondary_color || '#0d9488'} 100%)`
                }}>
                
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30" />

                  {/* Logo */}
                  <div className="absolute -bottom-6 left-4">
                    <div className="w-16 h-16 rounded-xl bg-white shadow-lg flex items-center justify-center overflow-hidden border-4 border-white">
                      {store.logo_url ?
                    <img src={store.logo_url} alt={store.name} className="w-full h-full object-cover" /> :

                    <Store className="h-8 w-8 text-muted-foreground" />
                    }
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="absolute top-3 right-3">
                    <Badge className={`gap-1 ${statusConfig.color}`}>
                      <StatusIcon className="h-3 w-3" />
                      {statusConfig.label}
                    </Badge>
                  </div>

                  {/* Actions */}
                  <div className="absolute top-3 left-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 bg-white/20 hover:bg-white/40 text-white">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem asChild>
                          <Link to={`/ecommerce/stores/${store.id}/edit`}>
                            <Edit className="h-4 w-4 mr-2" />{t("editStore", "Edit Store")}

                        </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/ecommerce/stores/${store.id}/edit?tab=theme`}>
                            <Palette className="h-4 w-4 mr-2" />{t("customizeTheme", "Customize Theme")}

                        </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/ecommerce/stores/${store.id}/edit?tab=settings`}>
                            <Settings className="h-4 w-4 mr-2" />{t("settings", "Settings")}

                        </Link>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Store Info */}
                <CardContent className="pt-10 pb-6 px-4">
                  <h3 className="font-semibold text-lg">{store.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                    {store.description || 'No description'}
                  </p>

                  {/* Store URL */}
                  <div className="flex items-center gap-2 mt-4 p-2 bg-muted/50 rounded-lg">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground truncate">{t("store", "/store/")}{store.slug}</span>
                    {store.status === 'published' &&
                  <a
                    href={`/store/${store.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto">
                    
                        <ExternalLink className="h-4 w-4 text-emerald-600 hover:text-emerald-700" />
                      </a>
                  }
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="p-3 bg-muted/30 rounded-lg text-center">
                      <Package className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
                      <p className="text-lg font-semibold">{store.products_count || 8}</p>
                      <p className="text-xs text-muted-foreground">{t("products", "Products")}</p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg text-center">
                      <ShoppingBag className="h-4 w-4 mx-auto text-muted-foreground mb-1" />
                      <p className="text-lg font-semibold">{store.orders_count || 50}</p>
                      <p className="text-xs text-muted-foreground">{t("orders", "Orders")}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    {store.status !== 'published' ?
                  <Button
                    className="flex-1 gap-2 bg-gradient-to-r from-emerald-500 to-teal-600"
                    onClick={() => handlePublish(store.id)}>
                    
                        <CheckCircle className="h-4 w-4" />{t("publish", "Publish")}

                  </Button> :

                  <a href={`/store/${store.slug}`} target="_blank" rel="noopener noreferrer" className="flex-1">
                        <Button variant="outline" className="w-full gap-2">
                          <Eye className="h-4 w-4" />{t("viewStore", "View Store")}

                    </Button>
                      </a>
                  }
                    <Link to={`/ecommerce/products?store=${store.id}`}>
                      <Button variant="outline" size="icon">
                        <Package className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>);

        })}
        </div>
      }
    </div>);

};

export default EcommerceStores;